import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Tag } from "lucide-react";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";

const CATEGORY_LABEL = {
  contractor: "For Contractors",
  worker: "For Workers",
  "thought-leadership": "Industry",
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("");

  useSEO({
    title: "Blog — Frontline Workforce Insights, Salary Data, Playbooks | Sarthi Associates",
    description: "Practical guides for EPC contractors and frontline workers — welder certifications, power plant salary rates, refinery shutdown playbooks, and India workforce data.",
    path: "/blog",
    jsonLd: { "@context": "https://schema.org", "@type": "Blog", "name": "Sarthi Associates Blog", "url": "https://sarthiassociates.com/blog" },
  });

  useEffect(() => {
    const params = filter ? { category: filter } : {};
    api.get("/blog", { params }).then((r) => setPosts(r.data));
  }, [filter]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-4 text-sm font-bold uppercase tracking-wider text-sarthi-primary">Sarthi Insights</div>
      <h1 className="font-display text-4xl font-black leading-tight text-sarthi-text sm:text-5xl">
        Guides, data & playbooks for India's frontline workforce
      </h1>

      <div className="mt-6 flex flex-wrap gap-2">
        {[["", "All"], ["contractor", CATEGORY_LABEL.contractor], ["worker", CATEGORY_LABEL.worker], ["thought-leadership", CATEGORY_LABEL["thought-leadership"]]].map(([key, label]) => (
          <button
            key={key || "all"}
            data-testid={`blog-filter-${key || "all"}`}
            onClick={() => setFilter(key)}
            className={`rounded-full border-2 px-4 py-2 text-sm font-bold ${filter === key ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white text-sarthi-text"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {posts.length === 0 ? (
        <div className="mt-10 text-center text-lg font-semibold text-sarthi-muted">No posts yet.</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="sarthi-card sarthi-card-press flex flex-col overflow-hidden"
            >
              {p.cover_image && (
                <div className="aspect-[16/9] w-full overflow-hidden bg-sarthi-accent">
                  <img src={p.cover_image} alt={p.title} className="h-full w-full object-cover" />
                </div>
              )}
              <div className="flex flex-1 flex-col p-5">
                <div className="text-xs font-bold uppercase tracking-wide text-sarthi-primary">
                  {CATEGORY_LABEL[p.category] || p.category}
                </div>
                <h2 className="mt-2 font-display text-xl font-extrabold leading-tight text-sarthi-text">{p.title}</h2>
                <p className="mt-2 flex-1 text-sm font-medium text-sarthi-muted">{p.excerpt}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-sarthi-secondary">
                  Read more <ArrowRight className="h-4 w-4" />
                </div>
                {p.tags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {p.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1 rounded-md bg-sarthi-accent px-2 py-0.5 text-xs font-bold text-sarthi-primary">
                        <Tag className="h-3 w-3" /> {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
